<template>
  <VueSlider
    v-model="range"
    class="mx-4 my-3"
    :enable-cross="false"
    :contained="true"
    adsorb
    :min="min"
    :max="max"
    tooltip="none"
    role="slider"
    tooltip-placement="bottom"
    @change="$emit('change')"
    @drag-start="$emit('drag-start')"
    @drag-end="$emit('drag-end')"
    @dragging="$emit('dragging')"
    @error="$emit('error')"
  >
    <template #dot="{ index }">
      <button
        class="cursor-pointer rounded-full border bg-black p-3 shadow-bottom focus:ring-2"
        :tabindex="0"
        :aria-label="$t('price_range.range_handler_label')"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :aria-valuenow="range[index]"
        @keydown.left.prevent="decrement(index)"
        @keydown.right.prevent="increment(index)"
      ></button>
    </template>
    <template #process="{ style }">
      <div class="vue-slider-process bg-black" :style="[style]" />
    </template>
  </VueSlider>
</template>

<script setup lang="ts">
import type { WritableComputedRef } from 'vue'
import 'vue-slider-component/dist-css/vue-slider-component.css'
// @ts-expect-error Could not find a declaration file for module 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'. '(...)' implicitly has an 'any' type.
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'

export type RangeTuple = [start: number, end: number]

interface RangeSliderProps {
  modelValue?: number[]
  min?: number
  max?: number
  valueFormatter?: (price: number) => string
}

const props = withDefaults(defineProps<RangeSliderProps>(), {
  modelValue: () => [3, 29999],
  min: 0,
  max: 100000,
  valueFormatter: undefined,
})

const emit = defineEmits<{
  change: []
  'drag-start': []
  'drag-end': []
  dragging: []
  'update:modelValue': [value: RangeTuple]
  error: []
}>()

const range: WritableComputedRef<number[]> = computed({
  get: () => props.modelValue || [3, 29999],
  set: (newValue: RangeTuple) => emit('update:modelValue', newValue),
})

const step = 100

const decrement = (index: number) => {
  const newRange = [...range.value]
  const currentValue = newRange[index]
  if (currentValue === undefined) {
    return
  }
  newRange[index] = Math.max(currentValue - step, props.min)
  range.value = newRange
}

const increment = (index: number) => {
  const newRange = [...range.value]
  const currentValue = newRange[index]
  if (currentValue === undefined) {
    return
  }
  newRange[index] = Math.min(currentValue + step, props.max)
  range.value = newRange
}
</script>

<style lang="postcss">
.vue-slider {
  height: 2px !important;
}
.vue-slider-rail {
  @apply bg-gray;
}
.vue-slider-dot {
  width: 35px !important;
  height: 35px !important;
}

/* This is a exception because the slider is taking in the line height of the text */
.vue-slider button {
  min-height: auto !important;
  padding: 0.75rem !important;
  line-height: 1 !important;
}
</style>
